import type {
  BillingInterval,
  LicenseStatus,
  RequestContext,
  SubscriptionTier
} from '../types'
import { logError } from './posthog'

const isNode = typeof process !== 'undefined' && process.versions?.node

let checkStripeSubscription: (context: RequestContext) => Promise<LicenseStatus>
let createCheckoutSession: (
  context: RequestContext,
  tier: SubscriptionTier,
  interval: BillingInterval
) => Promise<{ sessionId: string; url: string }>
let createCustomerPortalSession: (
  context: RequestContext
) => Promise<{ url: string }>
let isLicensed: (context: RequestContext) => Promise<boolean>
let getLicenseStatus: (context: RequestContext) => Promise<LicenseStatus>

if (isNode) {
  // --- Real Stripe logic ---
  const { storage } = require('@forge/api')
  const Stripe = require('stripe')
  const { track } = require('./posthog')
  const { BillingInterval, SubscriptionTier } = require('../types')

  type PriceIds = Record<
    typeof SubscriptionTier,
    Record<typeof BillingInterval, string>
  >

  const stripe = new Stripe(process.env.STRIPE_SECRET_KEY || '')

  const findCustomerBySiteUrl = async (siteUrl: string) => {
    const customers = await stripe.customers.search({
      query: `metadata['siteUrl']:'${siteUrl}'`
    })
    return customers.data[0] || null
  }

  const PRICE_IDS: PriceIds = {
    BASIC: {
      month: process.env.STRIPE_BASIC_MONTH_ID || '',
      year: process.env.STRIPE_BASIC_YEAR_ID || ''
    },
    PRO: {
      month: process.env.STRIPE_PRO_MONTH_ID || '',
      year: process.env.STRIPE_PRO_YEAR_ID || ''
    }
  }

  const STORAGE_KEYS = {
    LICENSE: 'license_status'
  }

  checkStripeSubscription = async (
    context: RequestContext
  ): Promise<LicenseStatus> => {
    try {
      const subscriptions = await stripe.subscriptions.search({
        query: `status:'active' AND metadata['siteUrl']:'${context.siteUrl}'`
      })
      if (subscriptions.data.length === 0) {
        return {
          isActive: false,
          tier: null,
          siteUrl: context.siteUrl,
          lastChecked: Date.now()
        }
      }
      const subscription = subscriptions.data[0]
      const priceId = subscription.items.data[0].price.id
      let tier: SubscriptionTier | null = null
      if (priceId === PRICE_IDS.PRO.month || priceId === PRICE_IDS.PRO.year) {
        tier = 'PRO'
      } else if (
        priceId === PRICE_IDS.BASIC.month ||
        priceId === PRICE_IDS.BASIC.year
      ) {
        tier = 'BASIC'
      }
      const status = {
        isActive: true,
        tier,
        siteUrl: context.siteUrl,
        lastChecked: Date.now()
      }
      await storage.set(STORAGE_KEYS.LICENSE, status)
      return status
    } catch (error) {
      logError('StripeHandler', 'Error checking Stripe subscription:', error)
      track(context, 'billing:stripe_subscription_check_failed', {
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      return {
        isActive: false,
        tier: null,
        siteUrl: context.siteUrl,
        lastChecked: Date.now()
      }
    }
  }

  getLicenseStatus = async (
    context: RequestContext
  ): Promise<LicenseStatus> => {
    try {
      const licenseStatus = (await storage.get(
        STORAGE_KEYS.LICENSE
      )) as LicenseStatus | null
      if (
        !licenseStatus ||
        licenseStatus.siteUrl !== context.siteUrl ||
        Date.now() - licenseStatus.lastChecked > 3600000
      ) {
        return checkStripeSubscription(context)
      }
      return licenseStatus
    } catch (error) {
      logError('StripeHandler', 'Error checking license status:', error)
      return {
        isActive: false,
        tier: null,
        siteUrl: context.siteUrl,
        lastChecked: Date.now()
      }
    }
  }

  isLicensed = async (context: RequestContext): Promise<boolean> => {
    const status = await getLicenseStatus(context)
    return status.isActive
  }

  createCheckoutSession = async (
    context: RequestContext,
    tier: SubscriptionTier,
    interval: BillingInterval
  ): Promise<{ sessionId: string; url: string }> => {
    try {
      const priceId = PRICE_IDS[tier][interval]
      if (!priceId) {
        throw new Error('Invalid tier or interval selected')
      }
      const siteName =
        context.siteUrl?.match(/https:\/\/([^.]+)\.atlassian\.net/)?.[1] ||
        context.siteUrl
      const customer =
        (await findCustomerBySiteUrl(context.siteUrl)) ||
        (await stripe.customers.create({
          name: siteName,
          metadata: {
            siteUrl: context.siteUrl,
            cloudId: context.cloudId
          }
        }))
      const basePath =
        context.localId?.replace('techradarAdmin', 'techradarGlobal') || ''
      const session = await stripe.checkout.sessions.create({
        mode: 'subscription',
        payment_method_types: ['card'],
        customer: customer.id,
        line_items: [
          {
            price: priceId,
            quantity: 1
          }
        ],
        success_url: `${context.siteUrl}/compass/app/${encodeURIComponent(basePath)}/success`,
        cancel_url: `${context.siteUrl}/compass/app/${encodeURIComponent(basePath)}/cancel`,
        metadata: {
          siteUrl: context.siteUrl,
          cloudId: context.cloudId,
          tier,
          interval
        },
        subscription_data: {
          metadata: {
            siteUrl: context.siteUrl,
            cloudId: context.cloudId,
            tier,
            interval
          }
        },
        payment_method_collection: 'always',
        allow_promotion_codes: true,
        locale: 'auto'
      })
      track(context, 'billing:checkout_session_created', {
        tier,
        interval,
        success: true
      })
      return {
        sessionId: session.id,
        url: session.url
      }
    } catch (error) {
      logError('StripeHandler', 'Stripe checkout session creation failed:', error)
      track(context, 'billing:checkout_session_failed', {
        tier,
        interval,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }

  createCustomerPortalSession = async (
    context: RequestContext
  ): Promise<{ url: string }> => {
    try {
      const subscriptions = await stripe.subscriptions.search({
        query: `status:'active' AND metadata['siteUrl']:'${context.siteUrl}'`
      })
      if (subscriptions.data.length === 0) {
        throw new Error('No active subscription found')
      }
      const customerId = subscriptions.data[0].customer as string
      const session = await stripe.billingPortal.sessions.create({
        customer: customerId,
        return_url: context?.extension?.url || '',
        locale: 'auto'
      })
      track(context, 'billing:portal_session_created', {
        success: true
      })
      return {
        url: session.url
      }
    } catch (error) {
      logError('StripeHandler', 'Stripe customer portal session creation failed:', error)
      track(context, 'billing:portal_session_failed', {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      throw error
    }
  }
} else {
  // --- Mock implementations for browser/local dev ---
  checkStripeSubscription = async () => ({
    isActive: false,
    tier: null,
    siteUrl: '',
    lastChecked: Date.now()
  })
  createCheckoutSession = async () => ({
    sessionId: 'mock-session',
    url: 'http://localhost/mock-stripe-checkout'
  })
  createCustomerPortalSession = async () => ({
    url: 'http://localhost/mock-stripe-portal'
  })
  isLicensed = async () => false
  getLicenseStatus = async () => ({
    isActive: false,
    tier: null,
    siteUrl: '',
    lastChecked: Date.now()
  })
}

export {
  checkStripeSubscription,
  createCheckoutSession,
  createCustomerPortalSession,
  isLicensed,
  getLicenseStatus
}
